import { GameState, Bounds, type DeathEvent, WeaponType } from '../types';
import { Renderer } from '../renderer/Renderer';
import { InputManager } from './InputManager';
import { Player } from '../entities/Player';
import { ScoringSystem } from './ScoringSystem';
import { CollisionSystem } from './CollisionSystem';
import { PatternManager } from './PatternManager';
import { WeaponOrbSpawner } from './WeaponOrbSpawner';
import { WeaponRegistry } from '../weapons/WeaponRegistry';
import type { Weapon } from '../weapons/Weapon';
import { NuclearBomb } from '../weapons/NuclearBomb';
import { Chainsaw } from '../weapons/Chainsaw';
import { COLOR_BACKGROUND, FRAME_TIME } from '../utils/constants';
import { getHighscores, addHighscore } from '../utils/storage';
import { GameEventLogger } from './GameEventLogger';
import { LevelManager } from './LevelManager';
import { BossManager } from './BossManager';
import type { LevelCategory } from './LevelSystem';


export class Game {
  private renderer: Renderer;
  private input: InputManager;
  private state: GameState = GameState.MENU;
  private lastFrameTime: number = 0;
  private isPaused: boolean = false;
  private accumulator: number = 0;

  private player: Player | null = null;

  private bounds: Bounds;
  private timeAlive: number = 0;

  private scoringSystem: ScoringSystem = new ScoringSystem();
  private collisionSystem: CollisionSystem;
  private levelManager: LevelManager = new LevelManager();
  private bossManager: BossManager = new BossManager();
  private patternManager: PatternManager = new PatternManager();
  private orbSpawner: WeaponOrbSpawner = new WeaponOrbSpawner();
  private activeWeapons: Weapon[] = [];
  private lastDeathEvent: DeathEvent | null = null;
  private eventLogger: GameEventLogger = new GameEventLogger();
  
  // UI Elements
  private menuElement: HTMLElement;
  private levelSelectElement: HTMLElement;
  private gameOverElement: HTMLElement;
  private permissionElement: HTMLElement;
  private newGameBtn: HTMLElement;
  private playAgainBtn: HTMLElement;
  private enableMotionBtn: HTMLElement;
  private finalScoreElement: HTMLElement;
  private newHighscoreElement: HTMLElement;
  private levelHudElement: HTMLElement | null;
  
  constructor() {
    this.renderer = new Renderer('game');
    this.input = new InputManager();
    this.bounds = this.renderer.getBounds();
    this.collisionSystem = new CollisionSystem(this.bounds);
    
    this.menuElement = document.getElementById('menu')!;
    this.levelSelectElement = document.getElementById('level-select')!;
    this.gameOverElement = document.getElementById('game-over')!;
    this.permissionElement = document.getElementById('permission-prompt')!;
    this.newGameBtn = document.getElementById('new-game-btn')!;
    this.playAgainBtn = document.getElementById('play-again-btn')!;
    this.enableMotionBtn = document.getElementById('enable-motion-btn')!;
    this.finalScoreElement = document.getElementById('final-score')!;
    this.newHighscoreElement = document.getElementById('new-highscore')!;
    this.levelHudElement = document.getElementById('level-hud');
    
    // Connect LevelManager to PatternManager and WeaponOrbSpawner
    this.patternManager.setLevelManager(this.levelManager);
    this.patternManager.setScoringSystem(this.scoringSystem);
    this.orbSpawner.setLevelManager(this.levelManager);
    
    this.setupEventListeners();
    
    // Setup scale slider
    this.setupScaleSlider();
    this.checkPlatform();
    this.gameLoop(0);
    this.displayMenuHighscores();
  }
  
  private checkPlatform(): void {
    const isDesktop = !('ontouchstart' in window) && 
                      !('DeviceOrientationEvent' in window);
    if (isDesktop) {
      this.showElement('desktop-warning', true);
      return;
    }
    this.checkOrientation();
    window.addEventListener('orientationchange', () => this.checkOrientation());
    window.addEventListener('resize', () => this.checkOrientation());
  }
  
  private checkOrientation(): void {
    const isLandscape = window.innerWidth > window.innerHeight;
    this.showElement('landscape-warning', isLandscape && this.state === GameState.PLAYING);
  }
  
  private showElement(id: string, show: boolean): void {
    const el = document.getElementById(id);
    if (el) el.style.display = show ? 'flex' : 'none';
  }

  private displayMenuHighscores(): void {
    const menuHighscoreList = document.getElementById('menu-highscores');
    if (menuHighscoreList) {
      const highscores = getHighscores();
      if (highscores.length === 0) {
        menuHighscoreList.innerHTML = '<li style="color: #666;">No scores yet</li>';
      } else {
        menuHighscoreList.innerHTML = highscores
          .slice(0, 10)
          .map((h, i) => `<li>${i + 1}. ${h.score}</li>`)
          .join('');
      }
    }
  }
  
  private setupEventListeners(): void {
    this.newGameBtn.addEventListener('click', () => this.showLevelSelect());
    this.playAgainBtn.addEventListener('click', () => this.showLevelSelect());
    this.enableMotionBtn.addEventListener('click', () => this.requestMotionPermission());
    
    window.addEventListener('resize', () => {
      this.bounds = this.renderer.getBounds();
    });
    
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.state === GameState.PLAYING) {
        this.pause();
      } else if (!document.hidden && this.state === GameState.PAUSED) {
        this.resume();
      }
    });
  }
  
  private setupScaleSlider(): void {
    const scaleSlider = document.getElementById('game-scale-slider') as HTMLInputElement;
    const scaleValue = document.getElementById('scale-value');
    
    if (scaleSlider && scaleValue) {
      const savedScale = localStorage.getItem('game-scale');
      const initialScale = savedScale ? parseInt(savedScale, 10) : 130;
      
      scaleSlider.value = initialScale.toString();
      scaleValue.textContent = `${initialScale}%`;
      this.renderer.setScale(initialScale / 100);
      
      this.bounds = this.renderer.getBounds();
      this.collisionSystem.updateBounds(this.bounds);
      
      scaleSlider.addEventListener('input', () => {
        const scale = parseInt(scaleSlider.value, 10);
        scaleValue.textContent = `${scale}%`;
        this.renderer.setScale(scale / 100);
        localStorage.setItem('game-scale', scale.toString());
        
        this.bounds = this.renderer.getBounds();
        if (this.collisionSystem) {
          this.collisionSystem.updateBounds(this.bounds);
        }
      });
    }
  }
  
  /**
   * Show level select screen
   */
  private showLevelSelect(): void {
    this.menuElement.classList.add('hidden');
    this.gameOverElement.classList.add('hidden');
    this.levelSelectElement.classList.remove('hidden');
    this.renderLevelSelect();
  }
  
  /**
   * Render level select buttons organized by category
   */
  private renderLevelSelect(): void {
    const levelsByCategory = this.levelManager.getLevelsForUI();
    const categories: LevelCategory[] = ['main', 'boss', 'portal', 'chaos', 'bullet'];

    const container = document.getElementById('level-buttons');
    if (!container) return;

    container.innerHTML = '';

    for (const category of categories) {
      const levels = levelsByCategory[category];
      if (levels.length === 0) continue;

      // Create category section
      const section = document.createElement('div');
      section.className = 'level-category';

      const categoryNames: Record<LevelCategory, string> = {
        main: '🎯 Main Path',
        boss: '👹 Boss Battles',
        portal: '🌀 Portal Path',
        chaos: '☢️ Chaos Path',
        bullet: '🔵 Bullet Path'
      };

      const categoryTitle = document.createElement('div');
      categoryTitle.className = 'category-title';
      categoryTitle.textContent = categoryNames[category];
      categoryTitle.style.color = levels[0].categoryColor;
      section.appendChild(categoryTitle);

      const buttonsContainer = document.createElement('div');
      buttonsContainer.className = 'category-buttons';

      for (const level of levels) {
        const isUnlocked = this.levelManager.isLevelUnlocked(level.id);
        const isCompleted = this.levelManager.isLevelCompleted(level.id);

        const btn = document.createElement('button');
        btn.className = `level-btn ${isUnlocked ? '' : 'locked'} ${isCompleted ? 'completed' : ''}`;
        btn.style.borderColor = level.themeColor;

        btn.innerHTML = `
          <span class="level-emoji">${level.iconEmoji}</span>
          <span class="level-name">${level.name}</span>
          <span class="level-desc">${isUnlocked ? level.description : '🔒 Locked'}</span>
          ${isCompleted ? '<span class="completed-badge">✓</span>' : ''}
          ${level.modifiers?.scoreMultiplier ? `<span class="multiplier">×${level.modifiers.scoreMultiplier}</span>` : ''}
        `;

        if (isUnlocked) {
          btn.addEventListener('click', () => this.selectLevel(level.id));
        }

        buttonsContainer.appendChild(btn);
      }

      section.appendChild(buttonsContainer);
      container.appendChild(section);
    }

    // Add back button
    const backBtn = document.createElement('button');
    backBtn.className = 'btn back-btn';
    backBtn.textContent = '← Back';
    backBtn.addEventListener('click', () => {
      this.levelSelectElement.classList.add('hidden');
      this.menuElement.classList.remove('hidden');
    });
    container.appendChild(backBtn);
  }
  
  /**
   * Select a level and start the game
   */
  private selectLevel(levelId: string): void {
    if (!this.levelManager.selectLevel(levelId)) {
      console.warn(`Failed to select level: ${levelId}`);
      return;
    }
    
    // Check permission
    if (this.input.needsPermissionRequest()) {
      this.levelSelectElement.classList.add('hidden');
      this.permissionElement.style.display = 'flex';
      return;
    }
    
    this.startGame();
  }
  
  private async requestMotionPermission(): Promise<void> {
    const granted = await this.input.requestPermission();
    
    if (granted) {
      this.permissionElement.style.display = 'none';
      this.startGame();
    } else {
      alert('Motion permission denied. The game requires device motion to play.');
    }
  }
  
  startGame(): void {
    this.state = GameState.PLAYING;
    this.timeAlive = 0;
    this.lastDeathEvent = null;
    this.lastFrameTime = performance.now();

    this.scoringSystem.start();

    this.collisionSystem.updateBounds(this.bounds);

    const centerX = this.bounds.width / 2;
    const centerY = this.bounds.height / 2;
    this.player = new Player(centerX, centerY);

    this.patternManager.clear();
    this.orbSpawner.initialize(this.bounds, this.player.getPosition());
    this.activeWeapons = [];
    this.eventLogger.startGame();

    // Configure boss manager based on current level
    const bossCfg = this.levelManager.getBossConfig();
    this.bossManager.reset();
    if (bossCfg && bossCfg.enabled) {
      this.bossManager.configure({
        enabled: true,
        types: bossCfg.types,
        spawnIntervalMs: bossCfg.spawnIntervalMs,
        initialDelayMs: bossCfg.initialDelayMs,
      });
    } else {
      this.bossManager.configure({ enabled: false, types: [] });
    }

    this.input.calibrateTiltBasis();

    this.menuElement.classList.add('hidden');
    this.levelSelectElement.classList.add('hidden');
    this.gameOverElement.classList.add('hidden');

    // Update level HUD
    this.updateLevelHud();
  }
  
  /**
   * Update the level HUD display
   */
  private updateLevelHud(): void {
    if (!this.levelHudElement) return;
    
    const level = this.levelManager.getCurrentLevel();
    const levelDisplay = this.levelHudElement.querySelector('#level-display');
    const levelProgress = this.levelHudElement.querySelector('#level-progress') as HTMLElement;
    
    if (levelDisplay) {
      levelDisplay.innerHTML = `
        <span class="level-emoji">${level.iconEmoji}</span>
        <span class="level-name" style="color: ${level.themeColor}">${level.name}</span>
      `;
    }
    
    if (levelProgress) {
      // Hide progress bar for manual level selection
      levelProgress.style.display = 'none';
    }
  }
  
  private handleGameOver(): void {
    const level = this.levelManager.getCurrentLevel();
    const multiplier = this.levelManager.getScoreMultiplier();
    const baseScore = this.scoringSystem.getScore();
    const finalScore = Math.floor(baseScore * multiplier);
    
    this.state = GameState.GAME_OVER;

    // Mark level as completed
    this.levelManager.completeLevel();

    this.finalScoreElement.innerHTML = `
      <span style="color: ${level.themeColor}">${level.iconEmoji} ${level.name}</span><br>
      Score: ${finalScore}
      ${multiplier > 1 ? `<span style="color: #FFD700; font-size: 0.7em;"> (×${multiplier})</span>` : ''}
    `;

    // Save highscore and check if it's a new high score
    const rank = addHighscore(finalScore);
    const isNewHighscore = rank > 0 && rank <= 3;
    
    // Show "New High Score!" message if applicable
    if (isNewHighscore) {
      this.newHighscoreElement.textContent = rank === 1 ? 'New #1 High Score!' : `New #${rank} High Score!`;
      this.newHighscoreElement.style.display = 'block';
    } else {
      this.newHighscoreElement.style.display = 'none';
    }

    // Display highscores on death screen
    const highscoreList = document.getElementById('gameover-highscores');
    if (highscoreList) {
      const highscores = getHighscores();
      highscoreList.innerHTML = highscores
        .slice(0, 10)
        .map((h, i) => {
          const isCurrentScore = rank > 0 && i + 1 === rank;
          const style = isCurrentScore ? ' style="color: #FFD700; font-weight: bold;"' : '';
          return `<li${style}>${i + 1}. ${h.score}</li>`;
        })
        .join('');
    }

    const deathReasonElement = document.getElementById('death-reason');
    if (deathReasonElement) {
      deathReasonElement.textContent = this.lastDeathEvent?.message ?? 'Unknown';
    }

    // Display event log
    const eventLogElement = document.getElementById('event-log');
    if (eventLogElement) {
      const events = this.eventLogger.getFormattedEvents();
      eventLogElement.innerHTML = events
        .map(e => `<li><span class="event-time">[${e.time}]</span> ${e.message}</li>`)
        .join('');
    }
    
    // Update button text
    this.playAgainBtn.textContent = 'Choose Level';
    
    this.gameOverElement.classList.remove('hidden');
  }

  pause(): void {
    this.isPaused = true;
  }

  resume(): void {
    this.isPaused = false;
    this.lastFrameTime = performance.now();
  }

  private gameLoop = (currentTime: number): void => {
    if (this.state !== GameState.PLAYING || this.isPaused) {
      requestAnimationFrame(this.gameLoop);
      return;
    }
    const deltaTime = currentTime - this.lastFrameTime;
    this.lastFrameTime = currentTime;

    this.accumulator += deltaTime;

    while (this.accumulator >= FRAME_TIME) {
      this.update(FRAME_TIME / 1000);
      this.accumulator -= FRAME_TIME;
    }

    this.render();
    requestAnimationFrame(this.gameLoop);
  };
  
  private update(dt: number): void {
    if (!this.player) return;

    this.timeAlive += dt;

    const velocity = this.input.getVelocity();
    this.player.update(dt, velocity, this.bounds);

    this.patternManager.update(dt, this.player.getPosition(), this.bounds);

    // If no dots and no pattern is actively spawning, throw in a new pattern
    const allDots = this.patternManager.getAllDots();
    if (allDots.length === 0 && !this.patternManager.isAnyPatternActivelySpawning()) {
      const nextType = this.patternManager.selectNextPattern();
      if (nextType) {
        this.patternManager.spawnPattern(nextType, this.player.getPosition(), this.bounds);
      }
    }

    this.orbSpawner.update(this.bounds, this.player.getPosition());

    // Combined dot list for collision/weapon/boss systems (pattern dots + baby dots)
    const babyDots = this.bossManager.getBabyDots();
    const combinedDots = babyDots.length > 0 ? [...allDots, ...babyDots] : allDots;

    this.collisionSystem.rebuildGrid(combinedDots);

    const orbs = this.orbSpawner.getOrbs();

    // Update bounced weapon orb positions
    for (const orb of orbs) {
      orb.updatePosition(dt, this.bounds);
    }

    const collidingOrb = this.collisionSystem.checkPlayerOrbCollision(this.player, orbs);
    if (collidingOrb) {
      const weaponType = collidingOrb.getWeaponType();

      // Check if this is a bounce weapon
      const isBounceWeapon = [
        WeaponType.NUCLEAR_BOMB,
        WeaponType.ELECTRIC_BOMB,
        WeaponType.FIREBALL_ORB,
        WeaponType.WORMHOLE
      ].includes(weaponType);

      if (isBounceWeapon) {
        // For bounce weapons: always create a new instance (allow multiple of same type)
        const weapon = WeaponRegistry.create(weaponType);
        if (weapon) {
          weapon.activate(this.player, combinedDots, collidingOrb.getPosition());
          this.activeWeapons.push(weapon);
          collidingOrb.pickup();
          this.orbSpawner.removeOrb(collidingOrb);
          this.eventLogger.logWeaponPickup(weaponType);
        }
      } else {
        // Non-bounce weapon: pick up immediately
        const weapon = WeaponRegistry.create(weaponType);
        if (weapon) {
          weapon.activate(this.player, combinedDots, collidingOrb.getPosition());
          this.activeWeapons.push(weapon);
          collidingOrb.pickup();
          this.orbSpawner.removeOrb(collidingOrb);
          this.eventLogger.logWeaponPickup(weaponType);
        }
      }
    }

    for (let i = this.activeWeapons.length - 1; i >= 0; i--) {
      const weapon = this.activeWeapons[i];

      // Pass orbs to NuclearBomb before update so it can destroy them in explosion
      if (weapon instanceof NuclearBomb) {
        weapon.setOrbs(orbs);
      }

      weapon.update(dt, this.player, combinedDots, this.bounds);

      // Remove any orbs destroyed by NuclearBomb explosion
      if (weapon instanceof NuclearBomb) {
        const destroyedOrbs = weapon.getDestroyedOrbs();
        for (const destroyedOrb of destroyedOrbs) {
          this.orbSpawner.removeOrb(destroyedOrb);
        }
      }

      // Check for player collision with active weapon (for bounceable weapons like Electric/Nuclear bomb)
      if (weapon.isActive()) {
        const playerPos = this.player.getPosition();
        const weaponPos = weapon.getPosition();
        const weaponRadius = weapon.getRadius();
        const playerRadius = this.player.hitboxRadius;

        const dx = weaponPos.x - playerPos.x;
        const dy = weaponPos.y - playerPos.y;
        const distSq = dx * dx + dy * dy;
        const radiiSum = weaponRadius + playerRadius;

        if (distSq < radiiSum * radiiSum) {
          const playerVelocity = this.input.getVelocity();
          weapon.handlePlayerCollision(this.player, playerVelocity);
        }
      }

      if (weapon instanceof NuclearBomb && weapon.hasKilledPlayer()) {
        this.eventLogger.logBouncedOrbDetonate('NUCLEAR_BOMB', weapon.getKilledDots());
        this.eventLogger.logPlayerDeath('Nuclear Bomb explosion');
        this.lastDeathEvent = {
          message: 'Killed by Nuclear Bomb explosion',
          type: 'nuclear_bomb',
          timestamp: Date.now()
        };
        this.handleGameOver();
        return;
      }

      if (weapon.isComplete()) {
        this.scoringSystem.addKills(weapon.getKilledDots());
        this.activeWeapons.splice(i, 1);
      }
    }

    // Update boss (handles spawning, buffs, damage, splitter babies)
    this.bossManager.update(dt, this.bounds, this.player.getPosition(), combinedDots, this.activeWeapons);

    // Check if player is invincible (has active chainsaw)
    const isInvincible = this.activeWeapons.some(w => w instanceof Chainsaw && !w.isComplete());

    if (!isInvincible) {
      // Boss collision (boss is lethal on contact)
      const bossHit = this.bossManager.checkPlayerCollision(
        this.player.getPosition(),
        this.player.hitboxRadius
      );
      if (bossHit) {
        this.eventLogger.logPlayerDeath('boss');
        this.lastDeathEvent = {
          message: 'Crushed by a boss',
          type: 'dot',
          timestamp: Date.now()
        };
        this.handleGameOver();
        return;
      }

      const collidingDot = this.collisionSystem.checkPlayerDotCollision(this.player);
      if (collidingDot) {
        if (collidingDot.isLethal()) {
          this.eventLogger.logPlayerDeath('red dot');
          this.lastDeathEvent = {
            message: 'Hit by a red dot',
            type: 'dot',
            timestamp: Date.now()
          };
          this.handleGameOver();
        } else if (collidingDot.isFrozen()) {
          collidingDot.kill();
          this.scoringSystem.addKills(1);
        }
      }
    }

    // Award score for boss kills via score system (treat boss kill as 10 kills)
    // Already handled implicitly by weapons.
  }
  
  private render(): void {
    this.renderer.clear(COLOR_BACKGROUND);

    const orbs = this.orbSpawner.getOrbs();
    for (let i = 0; i < orbs.length; i++) {
      orbs[i].render(this.renderer);
    }

    const allDots = this.patternManager.getAllDots();
    for (let i = 0; i < allDots.length; i++) {
      allDots[i].render(this.renderer);
    }

    for (let i = 0; i < this.activeWeapons.length; i++) {
      this.activeWeapons[i].render(this.renderer);
    }

    // Render bosses (and splitter babies) on top
    this.bossManager.renderBosses(this.renderer);

    if (this.player) {
      this.player.render(this.renderer);
    }

    this.renderer.flushBatches();
    this.renderer.endFrame();
  }
  
  getState(): GameState {
    return this.state;
  }
  
  getScore(): number {
    return this.scoringSystem.getScore();
  }

}
